(() => {
  const STORAGE_KEY = 'strapbandits:recently-viewed';
  const MAX_PRODUCTS = 4;

  const normalizeHandle = (value) => {
    if (typeof value !== 'string') return '';

    const handle = value.trim();
    if (!handle || handle.length > 255 || handle.includes('/') || handle.includes('\\')) return '';

    return handle;
  };

  const uniqueHandles = (values) => {
    if (!Array.isArray(values)) return [];

    return values.reduce((handles, value) => {
      const handle = normalizeHandle(value);
      if (handle && !handles.includes(handle)) handles.push(handle);
      return handles;
    }, []);
  };

  const readHandles = () => {
    try {
      return uniqueHandles(JSON.parse(window.localStorage.getItem(STORAGE_KEY))).slice(0, MAX_PRODUCTS);
    } catch (_error) {
      return [];
    }
  };

  const writeHandles = (handles) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(uniqueHandles(handles).slice(0, MAX_PRODUCTS)));
    } catch (_error) {
      // Browsers can disable storage; the section still fails closed and remains hidden.
    }
  };

  class SBRecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-initialized')) return;
      this.setAttribute('data-initialized', 'true');

      const rememberedHandles = readHandles();
      const currentHandle = normalizeHandle(this.dataset.currentProductHandle);
      const handlesToRender = rememberedHandles
        .filter((handle) => handle !== currentHandle)
        .slice(0, MAX_PRODUCTS);

      if (currentHandle) {
        writeHandles([currentHandle, ...rememberedHandles.filter((handle) => handle !== currentHandle)]);
      }

      if (!handlesToRender.length) {
        this.setAttribute('aria-busy', 'false');
        return;
      }

      this.renderProducts(handlesToRender);
    }

    async renderProducts(handles) {
      const grid = this.querySelector('[data-recently-viewed-grid]');
      if (!grid) return;

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      cards.filter(Boolean).forEach((card) => {
        const item = document.createElement('li');
        item.className = 'sb-recently-viewed__item';
        item.append(card);
        grid.append(item);
      });

      this.setAttribute('aria-busy', 'false');

      if (!grid.children.length) return;

      this.hidden = false;
      const status = this.querySelector('[data-recently-viewed-status]');
      const heading = this.querySelector('.sb-recently-viewed__heading')?.textContent.trim();
      if (status) status.textContent = `${heading || 'Recently viewed'}: ${grid.children.length}`;
    }

    async fetchCard(handle) {
      try {
        const rootUrl = this.normalizedRootUrl();
        const rendererId = this.dataset.rendererSectionId || 'sb-recent-product-card';
        const productUrl = `${rootUrl}products/${encodeURIComponent(handle)}`;
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('section_id', rendererId);

        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) return null;

        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const card = documentFragment.querySelector('[data-sb-recent-card]');
        if (!card || normalizeHandle(card.dataset.productHandle) !== handle) return null;

        return document.adoptNode(card);
      } catch (_error) {
        return null;
      }
    }

    normalizedRootUrl() {
      const rootUrl = this.dataset.rootUrl || '/';
      return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    }
  }

  if (!customElements.get('sb-recently-viewed')) {
    customElements.define('sb-recently-viewed', SBRecentlyViewed);
  }
})();
